import React, { useState } from 'react';
import copy from 'clipboard-copy';
import cx from 'classnames';

import { prettyPrint } from '@trqk/utils';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    code: any;
    displayMode?: 'dark' | 'light';
    maxWidth?: number;
    title?: string;
    showCodeByDefault?: boolean;
    showControls?: boolean;
}

/**
 * Renders a title and two buttons for a given chunk of code. One button allows the user to copy the
 * code to their clipboard, the other hides / shows a <pre> element containing the code below.
 * @param props props containing the `code` to be shown, as well as a `title` string. Optionally,
 * specify `showCodeByDefault={false}` to start out with the code hidden.
 */
export const CodeBlock = ({
    code,
    displayMode = 'dark',
    maxWidth = 800,
    title,
    showCodeByDefault = true,
    showControls = true,
}: Props): JSX.Element => {
    const [showCode, setShowCode] = useState(showCodeByDefault);
    const printedCode = prettyPrint(code);

    return (
        <div
            style={{
                maxWidth,
            }}
        >
            {title && (
                <div className="text-quinary-500 font-bold mt-2">{title}</div>
            )}
            {showControls && (
                <div className="flex my-2">
                    <button
                        type="button"
                        className="underline"
                        onClick={(): void => {
                            copy(printedCode);
                        }}
                    >
                        [Copy]
                    </button>
                    <button
                        type="button"
                        className="underline"
                        onClick={(): void => {
                            setShowCode(!showCode);
                        }}
                    >
                        [{showCode ? 'Hide' : 'Show'}]
                    </button>
                </div>
            )}
            <div className="w-full">
                <pre
                    className={cx('p-3 break-words', {
                        hidden: !showCode,
                        'text-white': displayMode === 'dark',
                        'bg-black': displayMode === 'dark',
                    })}
                    // for some reason, the .whitespace-pre-wrap class isn't working here, so I'm
                    // resorting to an inline style
                    style={{
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {printedCode}
                </pre>
            </div>
        </div>
    );
};
